<template>
    <div>
        <h4>
            <b-button variant="light"
                      @click="showFilter = !showFilter"
                      class="border-gray-light"
                      v-b-tooltip.hover
                      :title="(!showFilter ? 'Visa' : 'Dölj') + ' filter'"
                      size="sm">
                <font-awesome-icon icon="filter" />
            </b-button>
            {{ header }}
        <b-input-group size="sm"
                       prepend="inom"
                       append="dagar"
                       class="expiring-schedules--input-group--number-of-days">
            <b-form-input v-model="numberOfDays"
                          type="number"
                          autocomplete="off"
                          placeholder="x antal"
                          :max="365"
                          @change="inputChange">
            </b-form-input>
        </b-input-group>
        </h4>

        <b-input-group size="sm"
                       v-show="showFilter">
            <b-form-input id="filter-input"
                          v-model="filter"
                          type="search"
                          size="sm"
                          autocomplete="off"
                          placeholder="Filtrera....">
            </b-form-input>
            <b-input-group-append>
                <b-button :disabled="!filter" @click="filter = ''">
                    Rensa
                </b-button>
            </b-input-group-append>
        </b-input-group>

        <div class="widget-table-wrapper">
            <!-- TABLE -->
            <b-table :items="positions"
                     :fields="dynamicFields"
                     :current-page="currentPage"
                     :per-page="perPage"
                     :filter="filter"
                     :filter-included-fields="[
                        'kund',
                        'fastighet',
                        'artikel',
                        'description',
                        'place'
                      ]"
                     :sort-by.sync="sortBy"
                     :sort-desc.sync="sortDesc"
                     :sort-direction="sortDirection"
                     :busy="isLoading"
                     stacked="sm"
                     show-empty
                     small
                     @filtered="onFiltered"
                     @context-changed="onContextChanged">

                <template #cell(kund)="row">
                    <div class="overflow-hidden">
                        {{ row.item.kund }}
                    </div>
                </template>

                <template #cell(fastighet)="row">
                    <div class="overflow-hidden">
                        {{ row.item.fastighet }}
                    </div>
                </template>

                <template #cell(artikel)="row">
                    <div class="overflow-hidden">
                        {{ row.item.artikel }}
                    </div>
                </template>

                <template #cell(description)="row">
                    <div class="overflow-hidden">
                        {{ row.item.description }}
                    </div>
                </template>

                <template #cell(place)="row">
                    <div class="overflow-hidden">
                        {{ row.item.place }}
                    </div>
                </template>

                <template #cell(revisionsdatum)="row">
                    <div class="overflow-hidden">
                        {{ row.item.revisionsdatum | datetime('yyyy-MM-dd') }}
                    </div>
                </template>

                <template #empty="">
                    <h5 class="text-center">Inga resultat</h5>
                </template>

                <template #emptyfiltered="">
                    <div class="text-center">
                        <h5>Inga resultat</h5>
                        <h6>
                            <a href="#" @click.prevent="filter = ''">
                                Klicka här för att rensa aktiv filtrering
                            </a>
                        </h6>
                    </div>
                </template>

                <template #table-busy>
                    <div class="text-center mt-4">
                        <font-awesome-icon class="align-middle"
                                           icon="spinner"
                                           size="2x"
                                           spin />
                    </div>
                </template>
            </b-table>
        </div>

        <b-row>
            <b-col md="4"
                   sm="12">
                <b-form-group label-cols="5"
                              label-size="sm"
                              label="Per sida"
                              label-for="control-part-positions--per-page-select">
                    <b-form-select id="control-part-positions--per-page-select"
                                   v-model="perPage"
                                   :options="pageOptions"
                                   size="sm"
                                   style="float:left;">
                    </b-form-select>
                </b-form-group>
            </b-col>

            <b-col md="8"
                   sm="12">
                <b-pagination v-model="currentPage"
                              :total-rows="totalRows"
                              :per-page="perPage"
                              align="left"
                              size="sm"
                              hide-goto-end-buttons
                              hide-ellipsis>
                </b-pagination>
            </b-col>
        </b-row>
    </div>
</template>

<script>
    import { get } from '@/helpers/api';

    export default {
        name: 'ControlPartPositions',
        props: {
            header: String,
            organizationId: {
                type: Number,
                required: false
            },
            customerId: {
                type: [Number, String],
                required: false
            }
        },
        data() {
            return {
                isLoading: true,
                numberOfDays: 365,
                positions: [],
                fields: [
                    {
                        key: 'kund',
                        label: 'Kund',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'fastighet',
                        label: 'Fastighet',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'artikel',
                        label: 'Artikel',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    {
                        key: 'description',
                        label: 'Beskrivning',
                        sortable: false
                    },
                    {
                        key: 'place',
                        label: 'Plats',
                        sortable: false
                    },
                    {
                        key: 'revisionsdatum',
                        label: 'Revisionsdatum',
                        sortable: true,
                        sortDirection: 'asc'
                    }
                ],
                totalRows: 0,
                currentPage: 1,
                perPage: 10,
                pageOptions: [5, 10, 25, 50, 100],
                sortBy: 'revisionsdatum',
                sortDesc: false,
                sortDirection: 'asc',
                filter: null,
                showFilter: false
            }
        },
        computed: {
            dynamicFields() {
                return this.fields;
            },
            apiAction() {
                if (this.customerId) {
                    return `GetPositionsWithNextRevisionForCustomer/${this.customerId}?numberOfDays=${this.numberOfDays}`;
                }

                return `GetPositionsWithNextRevisionForOrganization?numberOfDays=${this.numberOfDays}`;
            }
        },
        methods: {
            onContextChanged(ctx) {
                if (ctx.perPage > this.totalRows) {
                    this.currentPage = 1;
                }
            },
            onFiltered(filteredItems) {
                this.totalRows = filteredItems.length;
            },
            async getData() {
                this.isLoading = true;

                await get('Widget', this.apiAction)
                    .then((x) => {
                        this.positions = x.data;
                        this.totalRows = this.positions.length;
                        this.isLoading = false;
                    })
                    .catch(() => {
                        this.isLoading = false;
                    });
            },
            inputChange() {
                if (this.numberOfDays) {
                    this.getData();
                }
            }
        },
        async mounted() {
            await this.getData();
        },
        watch: {
            numberOfDays(value) {
                if (value > 365) {
                    this.numberOfDays = 365;
                } else if (value < 1) {
                    this.numberOfDays = 1;
                }
            }
        }
    };
</script>

<style scoped>
    .table {
        margin-top: 8px;
    }

    .expiring-schedules--input-group--number-of-days {
        margin-top: 5px;
    }

    @media (min-width: 576px) {
        .expiring-schedules--input-group--number-of-days {
            max-width: 185px;
            float: right;
        }
    }
</style>
